import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Logo from '../components/Logo';
import { ArrowLeft, Home, Download, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map of friendly URLs to possible CMS slugs (try new format first, then fallback to old)
const SLUG_MAPPINGS = {
  '/privacy-policy': ['privacy-policy', 'privacy'],
  '/terms-of-service': ['terms-of-service', 'terms'],
  '/about': ['about'],
  '/contact': ['contact'],
  '/security': ['security'],
  '/hipaa-compliance': ['hipaa-compliance', 'hipaa'],
  '/business-associate-agreement': ['business-associate-agreement', 'baa'],
  '/documentation': ['documentation', 'api-docs'],
  '/blog': ['blog'],
  '/careers': ['careers'],
  '/press': ['press'],
  '/help': ['help'],
  '/cookies': ['cookies'],
};

const CMSPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine possible slugs to try
  const slugsToTry = useMemo(() => {
    // If we have a URL param (from /page/:slug routes), use it directly
    if (paramSlug) {
      return [paramSlug];
    }
    
    // Check if this is a mapped friendly URL
    if (SLUG_MAPPINGS[location.pathname]) {
      return SLUG_MAPPINGS[location.pathname];
    }
    
    // Fallback: Extract from pathname
    const pathSlug = location.pathname.replace(/^\//, '');
    return [pathSlug];
  }, [paramSlug, location.pathname]);

  useEffect(() => {
    const fetchPage = async () => {
      if (!slugsToTry.length) {
        setError('Page not found');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      // Try each possible slug until one works
      for (const slugToTry of slugsToTry) {
        try {
          console.log('Trying CMS page slug:', slugToTry);
          const response = await axios.get(`${API_URL}/api/cms-pages/public/${slugToTry}`);
          console.log('CMS page loaded:', response.data);
          setPage(response.data);
          setLoading(false);
          return; // Success - exit the loop
        } catch (err) {
          console.log(`Slug "${slugToTry}" not found, trying next...`);
          // Continue to next slug
        }
      }

      // All slugs failed
      console.error('All slug attempts failed');
      setError('Page not found');
      setLoading(false);
    };
    
    fetchPage();
  }, [slugsToTry]);

  useEffect(() => {
    if (page) {
      document.title = page.meta_title || page.title || 'MyMedMember';
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = page.meta_description || '';
    }
  }, [page]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  // Error state
  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Logo />
              <Button
                variant="outline"
                onClick={() => navigate('/')}
              >
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </header>

        {/* Error Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {error === 'Page not found' ? '404 - Page Not Found' : 'Error Loading Page'}
            </h1>
            <p className="text-gray-600 mb-8">
              {error === 'Page not found' 
                ? "The page you're looking for doesn't exist or has been moved."
                : "We're having trouble loading this page. Please try again later."}
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Determine template class
  const getTemplateClass = () => {
    switch (page.template) {
      case 'full-width':
        return 'max-w-full px-4 sm:px-6 lg:px-8';
      case 'landing':
        return 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8';
      default:
        return 'max-w-4xl mx-auto px-4 sm:px-6 lg:px-8';
    }
  };

  // Check if page should have download button
  const isDownloadablePage = page?.slug === 'business-associate-agreement' || page?.slug === 'baa';

  // Download page as PDF
  const downloadAsPDF = async () => {
    const element = document.getElementById('cms-content');
    if (!element) return;

    try {
      // Create canvas from HTML
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Save PDF
      const filename = (page?.title || 'document').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      pdf.save(`${filename}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo />
            <div className="flex gap-3">
              {isDownloadablePage && (
                <Button
                  variant="default"
                  onClick={downloadAsPDF}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
              >
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <main className={`${getTemplateClass()} py-12`}>
        {/* Page Title */}
        {page.template !== 'landing' && (
          <div className="mb-8 pb-8 border-b border-gray-200">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
              {page.title}
            </h1>
            <div className="flex items-center justify-between">
              <p className="text-gray-600">
                Last updated: {new Date(page.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              {isDownloadablePage && (
                <div className="flex items-center gap-2 text-teal-600">
                  <FileText className="h-5 w-5" />
                  <span className="text-sm">Downloadable Document</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rich Content */}
        <div 
          id="cms-content"
          className="cms-content prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: page.content }}
        />
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-600 text-sm">
              <p>© {new Date().getFullYear()} Clinicax Systems. All rights reserved.</p>
              <p className="text-gray-400 text-xs mt-1">MyMedMember™ is a trademark of Clinicax Systems.</p>
            </div>
            <div className="flex gap-6 text-sm">
              <button onClick={() => navigate('/privacy-policy')} className="text-gray-600 hover:text-teal-600">
                Privacy Policy
              </button>
              <button onClick={() => navigate('/terms-of-service')} className="text-gray-600 hover:text-teal-600">
                Terms of Service
              </button>
              <button onClick={() => navigate('/about')} className="text-gray-600 hover:text-teal-600">
                About Us
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* CMS Content Styles */}
      <style jsx global>{`
        .cms-content {
          color: #374151;
          line-height: 1.75;
        }
        .cms-content h1 {
          font-size: 2.25rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
          color: #111827;
        }
        .cms-content h2 {
          font-size: 1.875rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          color: #1f2937;
        }
        .cms-content h3 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
          color: #374151;
        }
        .cms-content p {
          margin-bottom: 1.25rem;
        }
        .cms-content ul,
        .cms-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1.25rem;
        }
        .cms-content ul {
          list-style-type: disc;
        }
        .cms-content ol {
          list-style-type: decimal;
        }
        .cms-content li {
          margin-bottom: 0.5rem;
        }
        .cms-content a {
          color: #0d9488;
          text-decoration: underline;
        }
        .cms-content a:hover {
          color: #0f766e;
        }
        .cms-content blockquote {
          border-left: 4px solid #0d9488;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #6b7280;
        }
        .cms-content code {
          background-color: #f3f4f6;
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875em;
          font-family: monospace;
        }
        .cms-content pre {
          background-color: #1f2937;
          color: #f9fafb;
          padding: 1rem;
          border-radius: 0.5rem;
          overflow-x: auto;
          margin: 1.5rem 0;
        }
        .cms-content pre code {
          background-color: transparent;
          padding: 0;
          color: inherit;
        }
        .cms-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
        }
        .cms-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .cms-content table th,
        .cms-content table td {
          border: 1px solid #e5e7eb;
          padding: 0.75rem;
          text-align: left;
        }
        .cms-content table th {
          background-color: #f9fafb;
          font-weight: 600;
        }
        .cms-content hr {
          border: none;
          border-top: 2px solid #e5e7eb;
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
};

export default CMSPage;
